// src/components/loads/hooks/useLoadActions.js
// PHASE 2: Auto-tags loads with parentCompanyId when driver is assigned or load is created
import { useCallback, useState } from 'react';
import { db, storage } from '../../../firebase';
import { doc, updateDoc, addDoc, deleteDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, getDownloadURL } from 'firebase/storage';
import { diffForAudit, describeChangeCount } from '../../../utils/auditDiff';
import { 
  logAudit, 
  prepareLoadData, 
  getStatusUpdateData, 
  canUserManageLoads,
  determineTenantId,
  userHasAnyRole
} from '../utils/loadHelpers';
import { archiveLoad } from '../../archive/services/archiveService';
import { teamForNewLoad, driverOwnTeam } from '../../../utils/loadTeam';
import { personName } from '../../../utils/loadCreator';
import { notifyUsers } from '../../../utils/notify';

// ============================================================================
// HELPER: Check if user is Super Admin
// ============================================================================
const isSuperAdmin = (user) => {
  if (!user) return false;
  const roles = Array.isArray(user.role) ? user.role : [user.role].filter(Boolean);
  return roles.includes('Super Admin');
};

/**
 * Custom hook to handle all load CRUD operations
 * @param {Object} loggedInUser - Current logged in user
 * @param {Array} currentLoads - Current loads array
 * @param {Array} drivers - Available drivers
 * @param {Array} brokers - Available brokers
 * @returns {Object} All load action handlers
 */
export const useLoadActions = (loggedInUser, currentLoads, drivers, brokers, { fillCustomerPhones = true } = {}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadToDelete, setLoadToDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const canManageLoads = canUserManageLoads(loggedInUser);
  const currentTenantId = determineTenantId(loggedInUser);

  /**
   * Editing a load that belongs to another team.
   *
   * Two things have to happen and they are deliberately separate: the person
   * making the change is asked first, and the team that owns the load is left a
   * note. Without the note the owning team finds their figures different with no
   * explanation; without the question it is far too easy to change somebody
   * else's work while meaning to change your own.
   */
  const confirmCrossTeamEdit = useCallback((oldDoc, whatChanged) => {
    if (!oldDoc?.otherTeam) return true;
    const owner = oldDoc.parentCompanyName || 'another team';
    return window.confirm(
      `This load belongs to ${owner}.\n\n` +
      `You are about to change: ${whatChanged}\n\n` +
      `${owner} will be shown that you changed this load.\n\nContinue?`
    );
  }, []);

  /**
   * The note itself, merged into whatever update is being written.
   *
   * One record per load rather than a growing list — a row in a table cannot
   * show a history, and the audit log already holds every before and after. This
   * says who last touched it, what they touched, and how many times it has
   * happened, which is enough to decide whether to pick up the phone.
   */
  const crossTeamMarker = useCallback((oldDoc, fields) => {
    if (!oldDoc?.otherTeam) return {};
    const previous = oldDoc.crossTeamEdit || null;
    return {
      crossTeamEdit: {
        byName: loggedInUser?.displayName || loggedInUser?.email || 'another team',
        byId: loggedInUser?.uid || null,
        at: new Date(),            // nested, so not serverTimestamp
        fields: Array.isArray(fields) ? fields : [fields],
        count: (previous?.count || 0) + 1,
      },
    };
  }, [loggedInUser]);

  // Handle status change
  const handleStatusChange = useCallback(async (docId, newStatus) => {
    if (!canManageLoads) {
      alert("You do not have permission to change status.");
      return;
    }
    
    const preDoc = currentLoads.find(l => l.docId === docId);
    if (!confirmCrossTeamEdit(preDoc, `status → ${newStatus}`)) return;

    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === docId);
      const updateData = {
        ...getStatusUpdateData(newStatus, oldDoc),
        ...crossTeamMarker(oldDoc, ['Status']),
      };

      await updateDoc(doc(db, "loads", docId), updateData);
  
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "status_change",
        targetType: "load", 
        targetId: docId, 
        tenantId: currentTenantId,
        details: { 
          loadIdDisplay: oldDoc?.load_id,
          field: "status", 
          oldValue: oldDoc?.status, 
          newValue: newStatus, 
          message: `Status changed to ${newStatus}` 
        }
      });
    } catch (err) { 
      alert("Failed to update status: " + err.message); 
      console.error("Failed to update status for docId " + docId + ":", err);
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loggedInUser, currentLoads, currentTenantId]);

  // Handle driver change
  // PHASE 2: Also stamps parentCompanyId on the load from the driver's record
  const handleDriverChange = useCallback(async (docId, newDriverId) => {
    if (!canManageLoads) return;
    
    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === docId);
      const selectedDriver = drivers.find(d => d.id === newDriverId);
      const assignedTruckId = selectedDriver?.assignedTruckId || "";

      // PHASE 2: Get parentCompanyId from the driver being assigned
      const driverParentCompanyId = selectedDriver?.parentCompanyId || null;
      const driverParentCompanyName = selectedDriver?.parentCompanyName || null;
      
      const updateData = { 
        driverId: newDriverId, 
        truckId: assignedTruckId, 
        updatedAt: serverTimestamp() 
      };

      // The load's dispatcher follows the driver's team link, rather than being
      // a second thing somebody sets by hand. Two independent answers to "whose
      // work is this?" drift apart, and then a dispatcher holds a load whose
      // driver they cannot even see. Covering for somebody else is a manual,
      // expiring grant — not a silently different value on one load.
      // Only when the driver actually has a dispatcher linked. Writing an empty
      // value otherwise would wipe a dispatcher somebody set by hand — which is
      // how every company that does not use team assignment works today.
      if (newDriverId && selectedDriver?.dispatcherId) {
        updateData.dispatcherId = selectedDriver.dispatcherId;
      }

      // The load's team goes where its dispatcher went — or, when it had no
      // team yet, to the driver's team. Otherwise the stamped team would keep
      // pointing at a team that no longer runs this load.
      // A teamId left over from a company move is not the driver's team.
      const selectedDriverTeam = driverOwnTeam(selectedDriver);
      if (newDriverId && selectedDriverTeam &&
          (selectedDriver.dispatcherId || !oldDoc?.teamId)) {
        updateData.teamId = selectedDriverTeam;
      }

      // PHASE 2: Tag load with driver's parent company — but ONLY if the load
      // doesn't already belong to one. Re-stamping an already-owned load
      // silently moves it out of the company that ran it, erasing the job from
      // their accounting history and driver statements.
      const didTagCompany = Boolean(driverParentCompanyId) && !oldDoc?.parentCompanyId;
      if (didTagCompany) {
        updateData.parentCompanyId = driverParentCompanyId;
        updateData.parentCompanyName = driverParentCompanyName;
        console.log(`🏢 Tagging previously untagged load with parentCompanyId: ${driverParentCompanyName} (${driverParentCompanyId})`);
      }

      await updateDoc(doc(db, "loads", docId), updateData);
      
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "driver_change",
        targetType: "load", 
        targetId: docId, 
        tenantId: currentTenantId,
        details: { 
          loadIdDisplay: oldDoc?.load_id,
          field: "driverId", 
          oldValue: oldDoc?.driverId, 
          newValue: newDriverId, 
          parentCompanyId: didTagCompany ? driverParentCompanyId : oldDoc?.parentCompanyId,
          parentCompanyName: didTagCompany ? driverParentCompanyName : oldDoc?.parentCompanyName,
          message: `Changed driver. Updated truck to "${assignedTruckId}".${didTagCompany ? ` Tagged with company: ${driverParentCompanyName}` : ''}`
        }
      });
    } catch (err) { 
      alert("Failed to update driver: " + err.message); 
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loggedInUser, drivers, currentLoads, currentTenantId]);

  // Handle truck change
  const handleTruckChange = useCallback(async (docId, newTruckId) => {
    if (!canManageLoads) return;

    const preDoc = currentLoads.find(l => l.docId === docId);
    if (!confirmCrossTeamEdit(preDoc, 'the truck')) return;

    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === docId);

      await updateDoc(doc(db, "loads", docId), {
        truckId: newTruckId,
        updatedAt: serverTimestamp(),
        ...crossTeamMarker(oldDoc, ['Truck']),
      });
      
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "truck_change",
        targetType: "load", 
        targetId: docId, 
        tenantId: currentTenantId,
        details: { 
          loadIdDisplay: oldDoc?.load_id,
          field: "truckId", 
          oldValue: oldDoc?.truckId, 
          newValue: newTruckId, 
          message: `Changed truck.` 
        }
      });
    } catch (err) { 
      alert("Failed to update truck: " + err.message); 
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loggedInUser, currentLoads, currentTenantId]);

  // Handle broker change
  const handleBrokerChange = useCallback(async (docId, newBrokerId) => {
    if (!canManageLoads) return;

    const preDoc = currentLoads.find(l => l.docId === docId);
    if (!confirmCrossTeamEdit(preDoc, 'the broker')) return;

    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === docId);

      await updateDoc(doc(db, "loads", docId), {
        brokerId: newBrokerId,
        updatedAt: serverTimestamp(),
        ...crossTeamMarker(oldDoc, ['Broker']),
      });
      
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "broker_change",
        targetType: "load", 
        targetId: docId, 
        tenantId: currentTenantId,
        details: { 
          loadIdDisplay: oldDoc?.load_id,
          field: "brokerId", 
          oldValue: oldDoc?.brokerId, 
          newValue: newBrokerId, 
          message: `Changed broker assignment.` 
        }
      });
    } catch (err) { 
      alert("Failed to update broker: " + err.message); 
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loggedInUser, currentLoads, currentTenantId]);

  // Handle dispatcher change - Admin only
  const handleDispatcherChange = useCallback(async (docId, newDispatcherId) => {
    // ✅ FIXED: Use userHasAnyRole helper to check for Admin or Super Admin
   if (!canManageLoads || !userHasAnyRole(loggedInUser, ['Admin', 'Main Admin', 'Super Admin'])) {
  alert("Only administrators can change dispatcher assignments.");
  return;
}
    
    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === docId);
      
      await updateDoc(doc(db, "loads", docId), { 
        dispatcherId: newDispatcherId, 
        updatedAt: serverTimestamp() 
      });
      
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "dispatcher_change",
        targetType: "load", 
        targetId: docId, 
        tenantId: currentTenantId,
        details: { 
          loadIdDisplay: oldDoc?.load_id,
          field: "dispatcherId", 
          oldValue: oldDoc?.dispatcherId, 
          newValue: newDispatcherId, 
          message: `Changed dispatcher assignment.` 
        }
      });

      // Both dispatchers hear about it in the bell (no email).
      const fromId = oldDoc?.dispatcherId || null;
      const toId = newDispatcherId || null;
      if (fromId !== toId) {
        const loadName = oldDoc?.load_id || 'a load';
        await notifyUsers({
          tenantId: currentTenantId,
          actorId: loggedInUser?.uid,
          notes: [
            fromId && { userId: fromId, message: `You are no longer the dispatcher for load ${loadName}` },
            toId && { userId: toId, message: `You are now the dispatcher for load ${loadName}` },
          ],
          extra: { loadId: oldDoc?.load_id || null, loadDocId: docId },
        });
      }
    } catch (err) { 
      alert("Failed to update dispatcher: " + err.message); 
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loggedInUser, currentLoads, currentTenantId]);

  // Handle delete click (show confirmation)
  const handleDeleteClick = useCallback((load) => {
    if (!canManageLoads) { 
      alert("You don't have permission."); 
      return; 
    }
    
    // Add safety check
    if (!load || !load.docId) {
      console.error('Invalid load object passed to handleDeleteClick:', load);
      alert("Error: Invalid load data");
      return;
    }
    
    setLoadToDelete(load.docId);
    setShowDeleteModal(true);
  }, [canManageLoads]);

  // Handle archive load (replaces delete)
  const handleDeleteLoad = useCallback(async () => {
    if (!canManageLoads || !loadToDelete) return;
    
    setIsProcessing(true);
    try {
      const oldDoc = currentLoads.find(l => l.docId === loadToDelete);
      const loadRef = doc(db, "loads", loadToDelete);
      
      // STEP 1: Unassign driver and truck BEFORE archiving
      if (oldDoc?.driverId || oldDoc?.truckId) {
        await updateDoc(loadRef, {
          driverId: null,
          truckId: null,
          updatedAt: serverTimestamp()
        });
        
        // Log the unassignment
        await logAudit({
          userId: loggedInUser?.uid, 
          userEmail: loggedInUser?.email, 
          action: "driver_unassigned_before_archive",
          targetType: "load", 
          targetId: loadToDelete, 
          tenantId: currentTenantId,
          details: { 
            loadIdDisplay: oldDoc?.load_id,
            message: `Driver and truck unassigned before archiving load ${oldDoc?.load_id || loadToDelete}`,
            previousDriverId: oldDoc?.driverId,
            previousTruckId: oldDoc?.truckId
          }
        });
      }
      
      // STEP 2: Archive the load
      await archiveLoad(loadToDelete, loggedInUser.uid, loggedInUser.email, currentTenantId);
      
      await logAudit({
        userId: loggedInUser?.uid, 
        userEmail: loggedInUser?.email, 
        action: "archive_load",
        targetType: "load", 
        targetId: loadToDelete, 
        tenantId: currentTenantId,
        // No spread of the whole load: archiving does not remove it, so every
        // field is still on the document itself. The message no longer promises
        // deletion after 30 days either — nothing in the project does that.
        // There is no scheduled job; archived loads stay until somebody deletes
        // one by hand from the Archive page.
        details: {
          loadIdDisplay: oldDoc?.load_id,
          message: `Archived load ${oldDoc?.load_id || loadToDelete}`,
          previousStatus: oldDoc?.status || null,
          amount: oldDoc?.amount ?? null,
        }
      });
      
      setShowDeleteModal(false);
      setLoadToDelete(null);
      
      alert(`Load archived.\n\nDriver and truck have been unassigned.\n\nNothing is deleted — the load keeps all its details and you can restore it from the Archive page at any time.`);
    } catch (err) { 
      alert("Failed to archive: " + err.message); 
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, loadToDelete, loggedInUser, currentLoads, currentTenantId]);

  // ============================================================
  // 🆕 Helper function to prepare dispatch document from PDF metadata
  // ============================================================
  const prepareDispatchDocumentFromPdf = useCallback(async (pdfMetadata, loadId) => {
    if (!pdfMetadata) return null;

    try {
      console.log("📎 Preparing dispatch document from PDF metadata:", pdfMetadata.originalFileName);

      let downloadUrl = pdfMetadata.downloadUrl;

      if (!downloadUrl && pdfMetadata.originalStoragePath) {
        try {
          const storageRef = ref(storage, pdfMetadata.originalStoragePath);
          downloadUrl = await getDownloadURL(storageRef);
          console.log("📎 Generated fresh download URL for PDF");
        } catch (urlError) {
          console.warn("⚠️ Could not get download URL for PDF:", urlError.message);
        }
      }

      const dispatchDocument = {
        url: downloadUrl || '',
        fileName: pdfMetadata.originalFileName || 'Imported Dispatch Sheet.pdf',
        fileType: pdfMetadata.fileType || 'application/pdf',
        uploadedAt: pdfMetadata.uploadedAt || new Date().toISOString(),
        uploadedBy: pdfMetadata.uploadedBy || loggedInUser?.email || 'system',
        uploadedById: pdfMetadata.uploadedById || loggedInUser?.uid || 'system',
        storagePath: pdfMetadata.originalStoragePath || '',
        documentType: 'dispatch',
        autoAttached: true,
        source: 'pdf_import'
      };

      console.log("📎 Dispatch document prepared:", {
        fileName: dispatchDocument.fileName,
        hasUrl: !!dispatchDocument.url,
        storagePath: dispatchDocument.storagePath
      });

      return dispatchDocument;
    } catch (error) {
      console.error("❌ Error preparing dispatch document from PDF:", error);
      return null;
    }
  }, [loggedInUser]);

  // Handle load form submit (create/update)
  // PHASE 2: Auto-tags new loads with parentCompanyId
  const handleLoadFormSubmit = useCallback(async (loadForm, isEditing, editDocId) => {
    if (!canManageLoads) return { success: false, error: "You do not have permission!" };

    if (!currentTenantId) {
      return { success: false, error: "Tenant information missing. Cannot create/update load." };
    }

    console.log("🔍 DEBUG: Creating/updating load with tenant ID:", currentTenantId);
    setIsProcessing(true);

    try {
      const data = prepareLoadData(loadForm, brokers, currentTenantId);

      // 🆕 Extract PDF metadata before removing it from load data
      const pdfMetadata = loadForm.attachedPdfMetadata;
      
      // Remove attachedPdfMetadata from the data being saved to Firestore
      delete data.attachedPdfMetadata;

      console.log("🔍 DEBUG: Load data being saved:", {
        load_id: data.load_id,
        tenantId: data.tenantId,
        isEditing: isEditing,
        driverCollectionAmount: data.driverCollectionAmount,
        brokerFeeCollection: data.brokerFeeCollection,
        storageFee: data.storageFee,
        factoringApplied: data.factoringApplied,
        factoringAmount: data.factoringAmount,
        factoringPercentage: data.factoringPercentage,
        hasPdfToAttach: !!pdfMetadata
      });
      
      if (isEditing && editDocId) {
        const { createdAt, ...updateData } = data;
        const oldDoc = currentLoads.find(l => l.docId === editDocId) || {};

        // The same guard the table controls use. Without it the whole feature
        // has a hole in the middle: a Main Admin could open another team's load,
        // change the amount and save, and that team would never be told.
        //
        // Only the fields that actually moved are named, so the note says
        // something useful rather than "this load was edited".
        if (oldDoc.otherTeam) {
          const watched = {
            amount: 'Amount',
            brokerFeeCollection: 'Broker fee',
            factoringFee: 'Factoring fee',
            paymentTerms: 'Payment terms',
            paymentMethod: 'Payment method',
            status: 'Status',
            pickupLocation: 'Pickup',
            deliveryLocation: 'Delivery',
            companyName: 'Carrier',
          };
          const changed = Object.keys(watched)
            .filter(k => updateData[k] !== undefined &&
                         String(updateData[k] ?? '') !== String(oldDoc[k] ?? ''))
            .map(k => watched[k]);

          if (changed.length > 0) {
            if (!confirmCrossTeamEdit(oldDoc, changed.join(', '))) {
              setIsProcessing(false);
              return { success: false, cancelled: true };
            }
            Object.assign(updateData, crossTeamMarker(oldDoc, changed));
          }
        }

        // PHASE 2: If driver changed during edit, tag parentCompanyId — but only
        // when the load has no owning company yet. An already-owned load must
        // not silently change hands; that erases it from the original company's
        // history and settlements.
        if (updateData.driverId && updateData.driverId !== oldDoc.driverId && !oldDoc.parentCompanyId) {
          const selectedDriver = drivers.find(d => d.id === updateData.driverId);
          if (selectedDriver?.parentCompanyId) {
            updateData.parentCompanyId = selectedDriver.parentCompanyId;
            updateData.parentCompanyName = selectedDriver.parentCompanyName;
            console.log(`🏢 Tagging previously untagged load with parentCompanyId: ${selectedDriver.parentCompanyName}`);
          }
        }
        
        await updateDoc(doc(db, "loads", editDocId), updateData);
        
        await logAudit({
          userId: loggedInUser?.uid, 
          userEmail: loggedInUser?.email, 
          action: "edit_load",
          targetType: "load", 
          targetId: editDocId, 
          tenantId: currentTenantId,
          details: (() => {
            const changes = diffForAudit(oldDoc, updateData);
            const fields = Object.keys(changes);
            return {
              loadIdDisplay: updateData.load_id,
              // Says what was touched without having to expand the entry.
              message: fields.length
                ? `Edited load ${updateData.load_id} — ${describeChangeCount(changes)}`
                : `Saved load ${updateData.load_id} with no changes`,
              changes,
              tenantId: currentTenantId,
            };
          })()
        });
        
        console.log("✅ Load updated successfully");
      } else {
        // ============================================================
        // NEW LOAD CREATION WITH AUTO PDF ATTACHMENT + PHASE 2 COMPANY TAGGING
        // ============================================================

        // A new load is always Booked. The add form no longer shows status,
        // so a status prefilled there (a PDF import, the extension) must not
        // slip through unseen. The driver can still be chosen on the form.
        data.status = 'Booked';

        // PHASE 2: Determine parentCompanyId for the new load
        // Priority: 1) From assigned driver, 2) From creating user's first assigned company
        let loadParentCompanyId = null;
        let loadParentCompanyName = null;

        // Try to get from the assigned driver first
        if (data.driverId) {
          const selectedDriver = drivers.find(d => d.id === data.driverId);
          if (selectedDriver?.parentCompanyId) {
            loadParentCompanyId = selectedDriver.parentCompanyId;
            loadParentCompanyName = selectedDriver.parentCompanyName;
            console.log(`🏢 New load tagged from driver: ${loadParentCompanyName}`);
          }
        }

        // If no driver assigned, use the creating user's first parent company
        if (!loadParentCompanyId && !isSuperAdmin(loggedInUser)) {
          const userCompanyIds = loggedInUser?.assignedParentCompanyIds || [];
          const userCompanyNames = loggedInUser?.assignedParentCompanyNames || [];
          if (userCompanyIds.length > 0) {
            loadParentCompanyId = userCompanyIds[0];
            loadParentCompanyName = userCompanyNames[0] || null;
            console.log(`🏢 New load tagged from creating user's company: ${loadParentCompanyName}`);
          }
        }
        
        // A new load's dispatcher follows its driver, for the same reason a
        // reassignment does. With no driver yet it falls back to whoever is
        // creating it, and corrects itself the moment a driver is chosen.
        const creatorDispatcherId = userHasAnyRole(loggedInUser, ["Dispatcher"]) ? loggedInUser.uid : "";
        const newLoadDriver = data.driverId ? drivers.find(d => d.id === data.driverId) : null;

        const dataForAdd = {
          ...data,
          dispatcherId: newLoadDriver?.dispatcherId || data.dispatcherId || creatorDispatcherId,
          // Who created it. Never stored before: the PDF function set it, and
          // saving through the form dropped it, so there was no record of who
          // created any load — which is why a missing dispatcher could not be
          // traced back to anyone.
          createdBy: loggedInUser?.uid || null,
          createdByEmail: loggedInUser?.email || null,
          // The name as well, so "Uploaded by" stays right even if this
          // person is later renamed or removed. See utils/loadCreator.
          createdByName: personName(loggedInUser) || null,
          createdAt: serverTimestamp(),
          isArchived: false,
          // Initialize document arrays
          dispatchDocuments: [],
          gatePassDocuments: [],
          // PHASE 2: Parent company tagging
          parentCompanyId: loadParentCompanyId,
          parentCompanyName: loadParentCompanyName,
          // The team whose work this is, fixed now so that people moving
          // between teams later does not carry the load off with them. See
          // utils/loadTeam. Null when the creator is on no team (a Super
          // Admin, or a company not using teams); the load's team is then
          // read from its dispatcher.
          // The driver's team when there is a driver — the dispatcher picker
          // works from it too — otherwise the creator's.
          teamId: driverOwnTeam(newLoadDriver) || teamForNewLoad(loggedInUser, loadParentCompanyId) || null,
        };

        // The tenant chose not to put customer phone numbers on loads
        // (Application Settings): drivers see whatever a load carries, so a new
        // load is saved without them, even when the import read them.
        if (!fillCustomerPhones) {
          dataForAdd.pickupContactPhone = '';
          dataForAdd.deliveryContactPhone = '';
        }

        // 🆕 If we have PDF metadata, prepare and attach the dispatch document
        if (pdfMetadata) {
          console.log("📎 Auto-attaching PDF as dispatch document...");
          
          const dispatchDocument = await prepareDispatchDocumentFromPdf(pdfMetadata, null);
          
          if (dispatchDocument) {
            dataForAdd.dispatchDocuments = [dispatchDocument];
            console.log("📎 PDF will be auto-attached as dispatch document");
          } else {
            console.warn("⚠️ Could not prepare dispatch document from PDF - continuing without attachment");
          }
        }
        
        const newLoadRef = await addDoc(collection(db, "loads"), dataForAdd);
        
        await logAudit({
          userId: loggedInUser?.uid, 
          userEmail: loggedInUser?.email, 
          action: "add_load",
          targetType: "load", 
          targetId: newLoadRef.id, 
          tenantId: currentTenantId,
          // Creation records that it happened, not the whole load.
          //
          // This used to spread the entire document in — every address, price,
          // vehicle and instruction — which buried the entries that matter. The
          // load itself holds all of that and is one click away; what the audit
          // trail needs is who made it and when. Changes are a different matter
          // and still record before and after, because that is where a question
          // like "who altered this price" actually gets answered.
          // Four facts, not the whole load: which load, what it pays, and
          // whether the paperwork came with it. The documents are stored as a
          // yes/no rather than the file metadata — whether one exists is the
          // question someone asks of an audit trail; the files themselves live
          // on the load.
          details: {
            loadIdDisplay: dataForAdd.load_id,
            amount: dataForAdd.amount ?? null,
            hasDispatchDocument: Array.isArray(dataForAdd.dispatchDocuments)
              && dataForAdd.dispatchDocuments.length > 0,
            hasGatePass: Array.isArray(dataForAdd.gatePassDocuments)
              && dataForAdd.gatePassDocuments.length > 0,
            tenantId: currentTenantId,
          }
        });
        
        console.log("✅ Load created successfully with ID:", newLoadRef.id, 
          pdfMetadata ? "(with PDF attached)" : "",
          loadParentCompanyId ? `(company: ${loadParentCompanyName})` : "(no company tag)"
        );
      }
      
      return { success: true };
    } catch (err) {
      console.error("❌ Error saving load:", err);
      return { success: false, error: err.message };
    } finally {
      setIsProcessing(false);
    }
  }, [canManageLoads, brokers, loggedInUser, drivers, currentLoads, currentTenantId, prepareDispatchDocumentFromPdf, fillCustomerPhones]);

  return {
    handleStatusChange,
    handleDriverChange,
    handleTruckChange,
    handleBrokerChange,
    handleDispatcherChange,
    handleDeleteClick,
    handleDeleteLoad,
    handleLoadFormSubmit,
    showDeleteModal,
    setShowDeleteModal,
    loadToDelete,
    isProcessing,
    canManageLoads
  };
};